// client/src/components/dashboard/account/EmergencyContactsManager.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaTimesCircle, FaTrashAlt, FaPlus, FaSyncAlt } from 'react-icons/fa';
import LoadingSpinner from '../../common/LoadingSpinner';

// --- Import REAL API functions ---
import {
    fetchEmergencyEmails, addEmergencyEmail, deleteEmergencyEmail, // Keep commented if not used yet
    fetchEmergencyPhones, addEmergencyPhone, deleteEmergencyPhone,  // Keep commented if not used yet
    sendEmergencyEmailVerification, // Import when ready to implement resend
    // sendEmergencyPhoneVerification // Import when ready to implement resend
} from '../../../api/authApi'; // Import API functions 

// --- Import PhoneInput ---
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import './EmergencyContactsManager.css';


// Define relationship options (values should be simple strings)
const relationshipOptions = [
    { value: 'family', labelKey: 'emergency.relationship.family' },
    { value: 'partner', labelKey: 'emergency.relationship.partner' },
    { value: 'friend', labelKey: 'emergency.relationship.friend' },
    { value: 'neighbor', labelKey: 'emergency.relationship.neighbor' },
    { value: 'coworker', labelKey: 'emergency.relationship.coworker' },
    { value: 'healthcare', labelKey: 'emergency.relationship.healthcare' },
    { value: 'other', labelKey: 'emergency.relationship.other' },
];

// Helper function to capitalize words in a name
const capitalizeName = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// --- Sub-component for the 'Add New' form (Refactored) ---
function AddEmergencyContactForm({ onAddSuccess, existingEmails, existingPhones }) {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const [contactType, setContactType] = useState('email'); // 'email' or 'phone'
    const [name, setName] = useState('');
    const [relationship, setRelationship] = useState('');
    const [emailValue, setEmailValue] = useState(''); // Separate state for email
    const [phoneValue, setPhoneValue] = useState(''); // Separate state for phone (raw value from PhoneInput)
    const [countryData, setCountryData] = useState({}); // Store country data if needed for formatting
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const resetForm = () => {
        setName(''); setRelationship(''); setEmailValue(''); setPhoneValue(''); setError(''); setContactType('email');
    };

    // --- Handler specific for PhoneInput to capture country data ---
    const handlePhoneInputChange = (value, country) => {
        setPhoneValue(value);
        setCountryData(country); // Store country data (includes dialCode)
        setError(''); // Clear error on change
    };
    // --- End Handler ---

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const currentVal = (contactType === 'email' ? emailValue : phoneValue).trim();

        if (!name || !relationship || !currentVal) {
            setError(t('emergency.errors.allFieldsRequired', 'Name, Relationship, and Email/Phone are required.'));
            return;
        }

        const formattedName = capitalizeName(name);
        let payload = { name: formattedName, relationship, language: i18n.language };
        let apiFunction;

        if (contactType === 'email') {
            // Validate Email Format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(currentVal)) {
                setError(t('emergency.errors.invalidEmail', 'Invalid Email format.')); return;
            }
            // Check Uniqueness
            if (existingEmails.some(contact => contact.email === currentVal)) {
                setError(t('emergency.errors.emailExists', 'This email already exists in your emergency contacts.')); return;
            }
            payload.email = currentVal;
            apiFunction = addEmergencyEmail;
        } else { // contactType is 'phone'
            // Basic length check
            if (currentVal.length < 7) {
                setError(t('emergency.errors.invalidPhone', 'Invalid Phone format.')); return;
            }

            // --- Apply Formatting Logic ---
            let formattedPhoneNumber = currentVal; // Start with the raw value from state
            const dialCode = countryData?.dialCode; // Get dial code from state
            console.log("[AddForm] Phone Submit: Raw=", currentVal, "DialCode=", dialCode);

            if (dialCode && currentVal.startsWith(dialCode)) {
                const nationalNumber = currentVal.substring(dialCode.length);
                if (nationalNumber.startsWith('0') && nationalNumber.length > 1) {
                    formattedPhoneNumber = dialCode + nationalNumber.substring(1); // Remove leading '0'
                    console.log(`[AddForm] Formatted phone: National=${nationalNumber}, Formatted=${formattedPhoneNumber}`);
                } else { console.log(`[AddForm] Phone formatting not needed for national part: ${nationalNumber}`); }
            } else { console.log(`[AddForm] Phone formatting skipped: dialCode missing or doesn't match start.`); }
            // --- End Formatting Logic ---

            // Check Uniqueness (using formatted number)
            if (existingPhones.some(contact => contact.phone_number === formattedPhoneNumber)) {
                setError(t('emergency.errors.phoneExists', 'This phone number already exists in your emergency contacts.')); return;
            }
            payload.phone_number = formattedPhoneNumber; // Send the formatted number
            apiFunction = addEmergencyPhone;
        }

        setLoading(true);
        try {
            console.log("Attempting to add emergency contact with payload:", payload);
            const response = await apiFunction(payload); // Call real API function
            console.log("Add emergency contact successful:", response);
            onAddSuccess(); resetForm();
        } catch (err) { /* ... Error handling ... */ }
        finally { setLoading(false); }
    };

    return (
        <div className="add-emergency-contact">
            <h4>{t('emergency.addTitle', 'Add New Emergency Contact')}</h4>
            {error && <div className="form-error">{error}</div>}
            <form onSubmit={handleSubmit}>
                {/* Row 1: Type Selector */}
                <div className="form-group contact-type-toggle">
                    <div className="radio-group"> {/* Wrapper for inline radios */}
                        <label>{t('emergency.contactType', 'Contact By')}:</label>
                        <input type="radio" id="add-type-email" name="contactType" value="email" checked={contactType === 'email'} onChange={() => { setContactType('email'); setError(''); }} />
                        <label htmlFor="add-type-email">{t('emergency.email', 'Email')}</label>
                        <input type="radio" id="add-type-phone" name="contactType" value="phone" checked={contactType === 'phone'} onChange={() => { setContactType('phone'); setError(''); }} />
                        <label htmlFor="add-type-phone">{t('emergency.phone', 'Phone')}</label>
                    </div>
                </div>
                {/* Row 2: Name, Relationship */}
                <div className="form-row add-form-row">
                    <div className="form-group">
                        <label htmlFor="add-name">{t('emergency.nameLabel', 'Full Name')}:</label>
                        <input type="text" id="add-name" value={name} onChange={(e) => setName(e.target.value)} required />
                    </div>
                    <div className="form-group">
                        <label htmlFor="add-relationship">{t('emergency.relationshipLabel', 'Relationship')}:</label>
                        <select id="add-relationship" value={relationship} onChange={(e) => setRelationship(e.target.value)} required >
                            <option value="" disabled>{t('emergency.selectRelationship', 'Select Relationship')}</option>
                            {relationshipOptions.map(opt => (<option key={opt.value} value={opt.value}>{t(opt.labelKey, /*...*/)}</option>))}
                        </select>
                    </div>

                    {/* Row 3: Conditional Input (Email/Phone) & Button */}
                    {/* Conditional Email/Phone Input */}
                    <div className="form-group">
                        <label htmlFor="add-value">{contactType === 'email' ? t('emergency.email', 'Email') : t('emergency.phone', 'Phone')}:</label>
                        {contactType === 'email' ? (
                            <input
                                type="email"
                                id="add-value"
                                value={emailValue}
                                onChange={(e) => setEmailValue(e.target.value)}
                                placeholder={t('emergency.emailPlaceholder', 'Enter email')}
                                required
                            />
                        ) : (
                            <PhoneInput // Use PhoneInput component
                                country={'dz'}
                                value={phoneValue}
                                onChange={handlePhoneInputChange} // Use specific handler
                                enableSearch={true} countryCodeEditable={false} specialLabel=""
                                inputProps={{ name: 'phone', id: 'add-value', required: true }}
                                containerClass="phone-input-container"
                                inputClass="phone-input-input"
                                placeholder={t('emergency.phonePlaceholder', 'Enter phone number')}
                            />
                        )}
                    </div>
                    {/* Add Button */}
                    <div className="form-group add-button-group">
                        <label>&nbsp;</label>
                        <button type="submit" className={`button-group form-action-button ${isRTL ? 'rtl' : ''}`} disabled={loading}>
                            {loading ? t('common.adding', 'Adding...') : <><FaPlus /> {t('common.add', 'Add')}</>}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

// Main component to manage and display lists
function EmergencyContactsManager() {
    const { t, i18n  } = useTranslation();
    const [emailContacts, setEmailContacts] = useState([]);
    const [phoneContacts, setPhoneContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [actionLoading, setActionLoading] = useState({ deleteEmail: null, deletePhone: null, resendEmail: null, resendPhone: null }); // State for specific action loading
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    const triggerRefetch = () => setRefreshTrigger(prev => prev + 1);

    // Fetch data using REAL functions
    useEffect(() => {
        let isMounted = true;
        const loadAllContacts = async () => {
            setLoading(true); setError(null);
            try {
                const [emailsResponse, phonesResponse] = await Promise.all([
                    fetchEmergencyEmails(), // Use REAL API function
                    fetchEmergencyPhones()  // Use REAL API function
                ]);
                if (isMounted) {
                    setEmailContacts(emailsResponse || []);
                    setPhoneContacts(phonesResponse || []);
                }
            } catch (err) { if (isMounted) setError(err.message || t('errors.api.requestFailed')); }
            finally { if (isMounted) setLoading(false); }
        };
        loadAllContacts();
        return () => { isMounted = false };
    }, [t, refreshTrigger]);

    const handleDeleteEmail = async (id) => {
        if (!id || actionLoading.deleteEmail === id) return; // Prevent double clicks
        // if (window.confirm(t('emergency.confirmDeleteEmail', `Are you sure you want to delete this email contact?`))) {
            setActionLoading(prev => ({ ...prev, deleteEmail: id })); // Set loading for this specific button
            setError(null); // Clear previous errors
            try {
                console.log("Attempting to delete email:", id);
                await deleteEmergencyEmail(id); // Use REAL API function
                triggerRefetch(); // Refresh list after delete
            } catch (err) { setError(err.message || t('errors.api.requestFailed')); }
            finally { setActionLoading(prev => ({ ...prev, deleteEmail: null })); } // Clear loading
        // }
    };
    const handleDeletePhone = async (id) => {
        if (!id || actionLoading.deletePhone === id) return;
        if (window.confirm(t('emergency.confirmDeletePhone', `Are you sure you want to delete this phone contact?`))) {
            setActionLoading(prev => ({ ...prev, deletePhone: id }));
            setError(null);
            try {
                console.log("Attempting to delete phone:", id);
                await deleteEmergencyPhone(id); // Use REAL API function
                triggerRefetch();
            } catch (err) { setError(err.message || t('errors.api.requestFailed')); }
            finally { setActionLoading(prev => ({ ...prev, deletePhone: null })); }
        }
    };

    // --- Resend Verification Handlers (Placeholders - Need API implementation) ---
    const handleResendEmailVerification = async (id) => {
        if (!id || actionLoading.resendEmail === id) return; // Prevent double clicks
        setActionLoading(prev => ({ ...prev, resendEmail: id })); // Set loading for this button
        setError(null); setMessage(''); // Clear previous messages
        console.log("Resend verification email button clicked for ID:", id);
        try {
            const currentLanguage = i18n.language; // Get current language
            console.log(`Calling sendEmergencyEmailVerification API for ID: ${id}, Language: ${currentLanguage}`);
            // --- Call the imported API function ---
            const response = await sendEmergencyEmailVerification(id, currentLanguage);
            // --- Set success message based on response ---
            setMessage(response.message || t('emergency.resendSuccess', 'Verification email resent successfully.'));
        } catch(err) {
             console.error("Error resending email verification:", err);
             setError(err.message || t('errors.api.requestFailed')); // Display translated error
        } finally {
             // Clear loading state for this specific button
             setActionLoading(prev => ({ ...prev, resendEmail: null }));
        }
   };

    // const handleResendPhoneVerification = async (id) => {
    //     if (!id || actionLoading.resendPhone === id) return;
    //     setActionLoading(prev => ({ ...prev, resendPhone: id }));
    //     setError(null);
    //     console.log("Resend verification phone:", id);
    //     try {
    //         // const response = await sendEmergencyPhoneVerification(id); // Call REAL API when implemented
    //         // alert(response.message || 'Verification sent');
    //         alert(`Placeholder: Resend verification API call for phone ID ${id}`); // Placeholder
    //     } catch (err) { setError(err.message || t('errors.api.requestFailed')); }
    //     finally { setActionLoading(prev => ({ ...prev, resendPhone: null })); }
    // };


    if (loading) return <LoadingSpinner />;

    return (
        <div className="emergency-contacts-manager">
            {error && <div className="form-error">{error}</div>}
            {message && <div className="form-success">{message}</div>}

            {/* Display Email Contacts */}
            <div className="contact-list-section">
                <h4>{t('emergency.emailsTitle', 'Emergency Emails')}</h4>
                {emailContacts.length > 0 ? (
                    <ul className="contact-list">
                        {emailContacts.map(contact => (
                            <li key={contact.emergency_email_id || `email-${contact.email}`}> {/* Use unique key */}
                                <div className="contact-info">
                                    <strong>{contact.name || 'N/A'}</strong>
                                    {contact.relationship && ` (${t(`emergency.relationship.${contact.relationship}`, contact.relationship)})`}
                                    &nbsp;&nbsp;-&nbsp;&nbsp;{contact.email}
                                </div>
                                <div className="contact-actions">
                                    {contact.is_verified ? (
                                        <FaCheckCircle className="verified-icon" title={t('contact.verified')} />
                                    ) : (
                                        <>
                                            <FaTimesCircle className="not-verified-icon" title={t('contact.notVerified')} />
                                            {/* Add Resend button */}
                                            <button onClick={() => handleResendEmailVerification(contact.emergency_email_id)} className="resend-verify-button" title={t('emergency.resendVerification', 'Resend Verification')}>
                                                <i className="fas fa-sync-alt"></i> {/* Or use a different React Icon */}
                                            </button>
                                        </>
                                    )}
                                    {/* Add Delete button */}
                                    <button onClick={() => handleDeleteEmail(contact.emergency_email_id)} className="delete-button" title={t('common.delete', 'Delete')}>
                                        <FaTrashAlt />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>{t('emergency.noEmails', 'No emergency emails added yet.')}</p> // Add key
                )}
            </div>

            {/* Display Phone Contacts */}
            <div className="contact-list-section">
                <h4>{t('emergency.phonesTitle', 'Emergency Phones')}</h4>
                {phoneContacts.length > 0 ? (
                    <ul className="contact-list">
                        {phoneContacts.map(contact => (
                            <li key={contact.emergency_phone_id || `phone-${contact.phone_number}`}> {/* Use unique key */}
                                <div className="contact-info">
                                    <strong>{contact.name || 'N/A'}</strong>
                                    {contact.relationship && ` (${t(`emergency.relationship.${contact.relationship}`, contact.relationship)})`}
                                    &nbsp;&nbsp;-&nbsp;&nbsp;{contact.phone_number}
                                </div>
                                <div className="contact-actions">
                                     {contact.is_verified ? ( <FaCheckCircle className="verified-icon" title={t('contact.verified')} /> ) : (
                                         <>
                                             <FaTimesCircle className="not-verified-icon" title={t('contact.notVerified')} />
                                              {/* Resend Button (Uncommented) */}
                                              <button
                                                  // Calls the updated handler
                                                  onClick={() => handleResendEmailVerification(contact.emergency_email_id)}
                                                  className="resend-verify-button"
                                                  title={t('emergency.resendVerification', 'Resend Verification')}
                                                  disabled={actionLoading.resendEmail === contact.emergency_email_id} // Disable specific button
                                              >
                                                  {/* Show spinner when this specific action is loading */}
                                                  {actionLoading.resendEmail === contact.emergency_email_id ? <LoadingSpinner size="small"/> : <FaSyncAlt />}
                                             </button>
                                         </>
                                     )}
                                      {/* Delete Button (Uncommented) */}
                                      <button
                                          onClick={() => handleDeletePhone(contact.emergency_phone_id)}
                                          className="delete-button"
                                          title={t('common.delete', 'Delete')}
                                          disabled={actionLoading.deletePhone === contact.emergency_phone_id}
                                      >
                                         {actionLoading.deletePhone === contact.emergency_phone_id ? <LoadingSpinner size="small"/> : <FaTrashAlt />}
                                      </button>
                                 </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>{t('emergency.noPhones', 'No emergency phone numbers added yet.')}</p> // Add key
                )}
            </div>

            <hr /> {/* Separator */}

            {/* Add New Contact Form */}
            <AddEmergencyContactForm
                onAddSuccess={triggerRefetch}
                existingEmails={emailContacts}
                existingPhones={phoneContacts}
            />

        </div>
    );
}
// Export the main manager component
export default EmergencyContactsManager;